import { createBrowserRouter, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import type { ReactNode } from 'react'
import { useSession } from '../hooks/useAppSession'
import type { ModuleId } from '../types/module'
import { AppShell }            from '../layouts/AppShell'
import { PolarisProvider }     from '../providers/PolarisProvider'
import { SubscriptionProvider } from '../providers/SubscriptionProvider'
import { ModuleGuard }         from '../components/guards/ModuleGuard'
import { ErrorBoundary }       from '../components/ErrorBoundary'
import { HomePage }            from '../pages/HomePage'
import { BoardingOverviewPage }  from '../pages/boarding/BoardingOverviewPage'
import { HistoryOverviewPage }   from '../pages/boarding/history/HistoryOverviewPage'
import { WorkflowHistoryPage }   from '../pages/boarding/history/WorkflowHistoryPage'
import { WorkspaceOverviewPage } from '../pages/workspace/WorkspaceOverviewPage'
import { PerformancePage }       from '../modules/dt-os/pages/PerformancePage'
import { SystemHealthPage }      from '../modules/dt-os/system-health/pages/SystemHealthPage'
import { WorkflowSectionPage }   from '../engine/workflow/WorkflowSectionPage'
import { CustomerServiceTicketsPage } from '../modules/dt-cs/pages/CustomerServiceTicketsPage'

// Lazy modules
const ReportsModule = lazy(() => import('../modules/reports'))
const DtOs  = lazy(() => import('../modules/dt-os'))
const DtSt  = lazy(() => import('../modules/dt-st'))
const DtCs  = lazy(() => import('../modules/dt-cs'))
const DtCf  = lazy(() => import('../modules/dt-cf'))
const DtSc  = lazy(() => import('../modules/dt-sc'))
const DtAn  = lazy(() => import('../modules/dt-an'))
const DtFo  = lazy(() => import('../modules/dt-fo'))
const DtFc  = lazy(() => import('../modules/dt-fc'))
const DtMo  = lazy(() => import('../modules/dt-mo'))
const DtExec = lazy(() => import('../modules/dt-exec'))
const AdminModule = lazy(() => import('../modules/admin'))

// Blocks non-admin/owner employees from accessing admin routes.
// Reads from SessionContext (provided by AppShell) — no extra network call.
function RequireAdmin({ children }: { children: ReactNode }) {
  const session = useSession()
  if (!session) return <Navigate to="/workspace-hub" replace />
  if (session.tenantRole !== 'admin' && session.tenantRole !== 'owner') {
    return <Navigate to="/workspace-hub" replace />
  }
  return <>{children}</>
}

function guard(moduleId: ModuleId, node: ReactNode) {
  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <ModuleGuard moduleId={moduleId}>{node}</ModuleGuard>
      </Suspense>
    </ErrorBoundary>
  )
}

function Root() {
  return (
    <PolarisProvider>
      <SubscriptionProvider>
        <AppShell />
      </SubscriptionProvider>
    </PolarisProvider>
  )
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    children: [
      { index: true,   element: <HomePage /> },
      { path: 'home',  element: <HomePage /> },

      // ── Boarding (flat routes — sub-sections have their own back arrows) ────
      { path: 'boarding',             element: <BoardingOverviewPage /> },
      { path: 'boarding/analytics/*',  element: guard('dt-an', <DtAn />) },
      { path: 'boarding/forecasting/*', element: guard('dt-fo', <DtFo />) },
      { path: 'boarding/financial/*',   element: guard('dt-fc', <DtFc />) },
      { path: 'boarding/marketing/*',   element: guard('dt-mo', <DtMo />) },
      { path: 'boarding/system-health', element: guard('dt-os', <SystemHealthPage />) },
      { path: 'boarding/history',         element: <HistoryOverviewPage /> },
      { path: 'boarding/history/:workflow', element: <WorkflowHistoryPage /> },

      // ── Workspace hub ───────────────────────────────────────────────────────
      { path: 'workspace-hub', element: <WorkspaceOverviewPage /> },

      // ── Workspace: Operations ───────────────────────────────────────────────
      { path: 'workspace/operations',   element: guard('dt-os', <PerformancePage />) },
      { path: 'workspace/operations/*', element: guard('dt-os', <DtOs />) },

      // ── Workspace sections — WorkflowSectionPage handles all sub-routes ──────
      // One wildcard route per section; WorkflowSectionPage reads the pathname
      // to determine which tab is active (overview | queue | activity).
      { path: 'workspace/confirmation/*',          element: guard('dt-cf', <WorkflowSectionPage workflowId="confirmation" />) },
      { path: 'workspace/sales-team/*',            element: guard('dt-st', <WorkflowSectionPage workflowId="sales" />) },
      { path: 'workspace/shipping-confirmation/*', element: guard('dt-sc', <WorkflowSectionPage workflowId="shipping_confirmation" />) },
      { path: 'workspace/customer-service/*',      element: guard('dt-cs', <WorkflowSectionPage workflowId="customer_service" />) },
      { path: 'workspace/delivery-tracking/*',     element: guard('dt-dt', <WorkflowSectionPage workflowId="delivery_tracking" />) },
      { path: 'workspace/customer-service/tickets', element: guard('dt-cs', <CustomerServiceTicketsPage />) },

      // ── Administration — admin + owner only ─────────────────────────────────
      { path: 'admin/*', element: <ErrorBoundary><Suspense fallback={null}><RequireAdmin><AdminModule /></RequireAdmin></Suspense></ErrorBoundary> },

      // ── Old module paths (keep working — no redirects this sprint) ──────────
      { path: 'dt-os/*',      element: guard('dt-os',  <DtOs />) },
      { path: 'dt-st/*',      element: guard('dt-st',  <DtSt />) },
      { path: 'dt-cf/*',      element: guard('dt-cf',  <DtCf />) },
      { path: 'reports/dt-cf',   element: <Navigate to="/dt-cf" replace /> },
      { path: 'reports/dt-cf/*', element: <Navigate to="/dt-cf" replace /> },
      { path: 'dt-sc/*',      element: guard('dt-sc',  <DtSc />) },
      { path: 'reports/dt-sc',   element: <Navigate to="/dt-sc" replace /> },
      { path: 'reports/dt-sc/*', element: <Navigate to="/dt-sc" replace /> },
      { path: 'dt-cs/*',      element: guard('dt-cs',  <DtCs />) },
      { path: 'reports/dt-cs',   element: <Navigate to="/dt-cs" replace /> },
      { path: 'reports/dt-cs/*', element: <Navigate to="/dt-cs" replace /> },
      { path: 'dt-fo/*',      element: guard('dt-fo',  <DtFo />) },
      { path: 'dt-mo/*',      element: guard('dt-mo',  <DtMo />) },
      { path: 'dt-fc/*',      element: guard('dt-fc',  <DtFc />) },
      { path: 'dt-an/*',      element: guard('dt-an',  <DtAn />) },
      {
        path: 'dt-exec/*',
        element: <ErrorBoundary><Suspense fallback={null}><ModuleGuard moduleId="dt-exec"><DtExec /></ModuleGuard></Suspense></ErrorBoundary>,
      },

      // ── Reports ────────────────────────────────────────────────────────────
      { path: 'reports/*', element: <ErrorBoundary><Suspense fallback={null}><ReportsModule /></Suspense></ErrorBoundary> },

      { path: '*', element: <Navigate to="/boarding" replace /> },
    ],
  },
])
